/**
 * bridge/app_info_controller.js — Hotwire Native "app-info" bridge component
 * (web side).
 *
 * Identifier: bridge--app-info (glob-derived from the bridge/ subdir; wire with
 * data-controller="bridge--app-info"). static component = "app-info" MUST equal
 * the iOS AppInfoComponent `class var name` and the Android-reached method name.
 *
 * Capability tier: exposes the native shell's version / build / platform / OS
 * version to the web so the app can version-gate features ("needs app >= 1.3")
 * and surface build info in support/diagnostics screens. Pure read — no UI, no
 * permissions, no sensors.
 *
 * It extends the Strada BridgeComponent (so it only connect()s inside the iOS
 * Hotwire Native shell and gets `this.send`), and contributes the `appInfo`
 * implementation triple to the window.ChippieBridge facade:
 *
 *   - ios:     this.send("get", {}, msg) over Strada to AppInfoComponent.swift;
 *              the reply { platform, version, build, osVersion } resolves the
 *              caller's promise.
 *   - android: window.ChippieNative.getAppInfo() returns a JSON string
 *              synchronously (a pure data read on the binder thread); parse it.
 *   - pwa:     resolve { platform: "pwa", version, build, osVersion } where
 *              version/build come from <html data-app-version/data-app-build>
 *              (server-rendered build stamps) when present, else empty, and
 *              osVersion is the browser UA. The web is the SSOT for its own
 *              "version" here; there is no native shell to ask.
 *
 * Callers do `await window.ChippieBridge.appInfo()` and get the info object, OR
 * wire a button with data-action="bridge--app-info#get" and listen for the
 * dispatched Stimulus event on this controller's element:
 *   - bridge--app-info:retrieved  detail: { platform, version, build, osVersion }
 *   - bridge--app-info:error
 *
 * Template wiring:
 *   <div data-controller="bridge--app-info"
 *        data-action="bridge--app-info:retrieved->support-form#fillBuild">
 *     <button data-action="bridge--app-info#get">Show app version</button>
 *   </div>
 *
 * The Android/PWA impls carry no per-instance state, so they are registered
 * standalone at module load; the iOS impl (needing a live Strada send) is wired
 * in connect(). The facade (chippie-bridge.js) owns platform detection and the
 * fallback chain, so callers never branch on platform.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Android: synchronous JSON-string read from the @JavascriptInterface. Kept in
// sync with the Kotlin handler (ChippieNativeBridge.getAppInfo).
function androidAppInfo() {
    return new Promise((resolve, reject) => {
        try {
            const raw = window.ChippieNative.getAppInfo();
            resolve(JSON.parse(raw));
        } catch (_e) {
            reject(new Error("app-info-unavailable"));
        }
    });
}

// PWA: no native shell to ask. Report the web build stamps (server-rendered onto
// <html>) and the browser UA so support still gets something useful.
// In the Safari browser on iOS the browser owns back: its swipe-back animates a
// snapshot of the previous page before popstate fires, so the web must neither
// arm its own edge-swipe nor animate the restoration. A home-screen web app has
// no such gesture (nor does any Android browser), so the web owns back there.
function browserOwnsBack() {
    const ios = /iP(hone|ad|od)/.test(navigator.userAgent || "") ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const standalone = window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
    return ios && !standalone;
}

function pwaAppInfo() {
    const root = document.documentElement;
    return Promise.resolve({
        platform: "pwa",
        version: root.dataset.appVersion || "",
        build: root.dataset.appBuild || "",
        osVersion: navigator.userAgent || "",
        ownsBack: browserOwnsBack(),
    });
}

// iOS placeholder until a controller instance connects with a live Strada send;
// rejects so callers fail cleanly rather than hanging if selected before connect.
function iosPlaceholder() {
    return Promise.reject(new Error("app-info-unavailable"));
}

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("appInfo", {
        ios: iosPlaceholder,
        android: androidAppInfo,
        pwa: pwaAppInfo,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "app-info";

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("appInfo", {
            ios: () =>
                new Promise((resolve, reject) => {
                    this.send("get", {}, (message) => {
                        const data = message.data || {};
                        if (!data.platform) {
                            reject(new Error("app-info-unavailable"));
                        } else {
                            resolve(data);
                        }
                    });
                }),
            android: androidAppInfo,
            pwa: pwaAppInfo,
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale `this.send` is never invoked.
        registerStandalone();
        super.disconnect();
    }

    // data-action="bridge--app-info#get" trigger — fetch app info, then dispatch
    // a Stimulus event other controllers/forms can react to.
    get(event) {
        if (event) {
            event.stopImmediatePropagation();
            event.preventDefault();
        }
        window.ChippieBridge.appInfo().then(
            (info) => this.dispatch("retrieved", { detail: info }),
            () => this.dispatch("error")
        );
    }
}
